import { useState, useEffect } from "preact/hooks";
import { isProdHost } from "@/lib/web";
import { obfuscate, deobfuscate } from "@/lib/strings";
import { doLog } from "@/lib/globals";

function shouldObfuscate() {
  return isProdHost;
}

// ======================================================================================|
//  MARK: Keys

export const STORAGE_KEYS = {
  completedAssessmentKey: "ca",
  activityReportViewedKey: "arv",
  turnstileToken: "tt",
  turnstileTokenSent: "flag_tt_sent",
  appLoadEventSent: "flag_app_load_event_sent",
  messages: "msg_log",
  countryCode: "cc",
  inActivity: "ia",
  targetLangIsRtl: "tl_rtl",
  lcidKey: "id",
};

// ======================================================================================|
//  MARK: Local Storage
//  NB: local storage is used to persist state across page loads within the same browser

export function useLocalStorage(key, initialValue) {
  try {
    const [value, setValue] = useState(() => {
      const storedValue = localStorageGet(key);
      if (!storedValue) return initialValue;
      return storedValue;
    });

    useEffect(() => {
      localStorageSet(key, value);
    }, [key, value]);

    return [value, setValue];
  } catch (error) {
    if (doLog()) console.error("Error reading from local storage key:", error);
    return [initialValue, () => {}];
  }
}

export function localStorageSet(key, value) {
  try {
    // if (doLog()) console.log("localStorageSet", key, value);
    // Handle null/undefined values
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
      return;
    }

    const valueJson = JSON.stringify(value);
    const valueObfuscated = shouldObfuscate() ? obfuscate(valueJson) : valueJson;
    localStorage.setItem(key, valueObfuscated);
  } catch (error) {
    console.warn(`Error writing to local storage key "${key}":`, error);
  }
}

export function localStorageGet(key) {
  try {
    const storedValue = localStorage.getItem(key);
    if (!storedValue) return null; // Return null for missing values

    const valueDecoded = shouldObfuscate() ? deobfuscate(storedValue) : storedValue;
    return JSON.parse(valueDecoded);
  } catch (error) {
    console.warn(`Error reading from local storage key "${key}":`, error);
    return null; // Return null on parse errors
  }
}

export function clearLocalStorage() {
  try {
    localStorage.clear();
  } catch (error) {
    if (doLog()) console.error("Error clearing local storage:", error);
  }
}

// ======================================================================================|
//  MARK: Session Storage
//  NB: session storage is used to persist state across page loads within the same tab

export function useSessionStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const storedValue = sessionStorageGet(key);
      if (!storedValue) return initialValue;
      return storedValue;
    } catch (error) {
      if (doLog())
        console.error(`Error reading from session storage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorageSet(key, value);
    } catch (error) {
      if (doLog()) console.error(`Error writing to session storage key "${key}":`, error);
    }
  }, [key, value]);

  return [value, setValue];
}

export function sessionStorageSet(key, value) {
  try {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      sessionStorage.removeItem(key);
      return;
    }

    const valueJson = JSON.stringify(value);
    const valueObfuscated = shouldObfuscate() ? obfuscate(valueJson) : valueJson;
    sessionStorage.setItem(key, valueObfuscated);
  } catch (error) {
    if (doLog()) console.error(`Error writing to session storage key "${key}":`, error);
  }
}

export function sessionStorageGet(key) {
  try {
    const storedValue = sessionStorage.getItem(key);
    if (!storedValue) return null;

    const valueDecoded = shouldObfuscate() ? deobfuscate(storedValue) : storedValue;
    return JSON.parse(valueDecoded);
  } catch (error) {
    if (doLog()) console.error(`Error reading from session storage key "${key}":`, error);
    return null;
  }
}

export function clearSessionStorage() {
  try {
    if (doLog()) console.log("clearSessionStorage");
    sessionStorage.clear();
  } catch (error) {
    if (doLog()) console.error("Error clearing session storage:", error);
  }
}
